/**
 * Battlefield card actions
 *
 * Centralized logic for executing actions on battlefield cards.
 * Used by both keyboard hotkeys and context menu clicks.
 */

import * as Y from 'yjs';
import { WhiteboardCard } from './types';
import { KeywordToken } from '@/features/keyword-tokens/types';
import { nodeCenter } from './nodeAttachment';
import { useContextMenuStore } from '@/features/hotkeys/contextMenuStore';
import { logAction, cardLogName } from '@/features/action-log/actionLog';
import { spawnTokenAtPosition, getMaxZIndex, detachTokens } from './spawnToken';
import { firstFreeCascadeSlot, CASCADE_OFFSET } from './cascade';
import {
  moveCardToHand,
  moveCardToDiscard,
  moveCardToExile,
  moveCardToDeckTop,
  moveCardToDeckBottom,
} from './battlefieldActions';
import { makeCardId } from '@/shared/utils/ids';
import { resolvePlayerName } from '@/shared/utils/resolvePlayerName';

// Name to use for a card's owner in a log entry describing an action taken
// on someone else's card — null when the actor owns the card themselves, so
// callers can branch on possessive vs. first-person phrasing (mirrors
// Player.modifyHealth's self-vs-target split).
function opponentOwnerName(yDoc: Y.Doc, card: WhiteboardCard, actorId: string): string | null {
  return card.ownerId === actorId ? null : resolvePlayerName(yDoc, card.ownerId);
}

// Copies cascade down-right from the card they came from, solitaire-style. A
// fixed offset would drop every copy after the first on the same spot (copying
// the same card twice reads the same source position), so the search starts one
// step out — a copy must never land on its source — and `firstFreeCascadeSlot`
// walks on from there. The Nth copy of a card lands N steps down the cascade,
// and copying a copy continues the same run.
function nextCascadePosition(card: WhiteboardCard, yCards: Y.Map<WhiteboardCard>) {
  return firstFreeCascadeSlot(yCards, {
    x: card.x + CASCADE_OFFSET,
    y: card.y + CASCADE_OFFSET,
  });
}

export function executeBattlefieldCardAction(
  action: string,
  cardId: string,
  yCards: Y.Map<WhiteboardCard>,
  yTokens: Y.Map<KeywordToken>,
  playerId: string,
) {
  const yDoc = yCards.doc;

  if (action === 'untapAll') {
    // "Untap all" resets every one of your cards to 0° — clearing both the 90°
    // tap and the 45° summoning-sick tilt (the two are mutually exclusive, so a
    // card carries at most one). Skip cards already flat to avoid no-op writes.
    yCards.forEach((c, cId) => {
      if (c.ownerId === playerId && (c.isTapped || c.isSick)) {
        yCards.set(cId, { ...c, isTapped: false, isSick: false });
      }
    });
    if (yDoc) {
      logAction(yDoc, { actorId: playerId, type: 'untap_all', text: 'untapped all cards' });
    }
    return;
  }

  const card = yCards.get(cardId);
  if (!card) return;

  switch (action) {
    case 'tap': {
      // card.isTapped reflects the state *before* the toggle
      const verb = card.isTapped ? 'untapped' : 'tapped';
      // Tap and summoning-sickness are mutually exclusive rotations (a card is
      // in one physical position), so tapping clears the 45° sick tilt.
      yCards.set(cardId, { ...card, isTapped: !card.isTapped, isSick: false });
      if (yDoc) {
        const ownerName = opponentOwnerName(yDoc, card, playerId);
        logAction(yDoc, {
          actorId: playerId,
          type: 'tap',
          text: ownerName ? `${verb} ${ownerName}'s ${cardLogName(card)}` : `${verb} ${cardLogName(card)}`,
        });
      }
      break;
    }
    case 'sick': {
      // card.isSick reflects the state *before* the toggle. Marking a card
      // summoning-sick clears tap — the two tilts are mutually exclusive.
      const willBeSick = !card.isSick;
      yCards.set(cardId, { ...card, isSick: willBeSick, isTapped: false });
      if (yDoc) {
        const ownerName = opponentOwnerName(yDoc, card, playerId);
        const verb = willBeSick ? 'marked' : 'cleared summoning sickness on';
        const text = willBeSick
          ? (ownerName ? `marked ${ownerName}'s ${cardLogName(card)} summoning sick` : `marked ${cardLogName(card)} summoning sick`)
          : (ownerName ? `${verb} ${ownerName}'s ${cardLogName(card)}` : `${verb} ${cardLogName(card)}`);
        logAction(yDoc, { actorId: playerId, type: 'sick', text });
      }
      break;
    }
    case 'flip': {
      // card.isFlipped reflects the state *before* the toggle.
      const willBeFlipped = !card.isFlipped;
      yCards.set(cardId, { ...card, isFlipped: willBeFlipped });
      if (yDoc) {
        const ownerName = opponentOwnerName(yDoc, card, playerId);
        // Flipping face down must not name the card it's hiding; flipping
        // face up reveals it to everyone at once, so the name is now public.
        let text: string;
        if (willBeFlipped) {
          text = ownerName ? `flipped ${ownerName}'s card face down` : 'flipped a card face down';
        } else {
          text = ownerName ? `flipped ${ownerName}'s ${card.name} face up` : `flipped ${card.name} face up`;
        }
        logAction(yDoc, { actorId: playerId, type: 'flip', text });
      }
      break;
    }
    case 'copy': {
      const maxZIndex = getMaxZIndex(yCards, yTokens);
      const { x, y } = nextCascadePosition(card, yCards);
      const newCard: WhiteboardCard = {
        ...card,
        id: makeCardId(),
        ownerId: playerId,
        x,
        y,
        zIndex: maxZIndex + 1,
        counters: [...card.counters],
      };
      yCards.set(newCard.id, newCard);
      if (yDoc) {
        const ownerName = opponentOwnerName(yDoc, card, playerId);
        logAction(yDoc, {
          actorId: playerId,
          type: 'copy',
          text: ownerName ? `copied ${ownerName}'s ${cardLogName(card)}` : `copied ${cardLogName(card)}`,
        });
      }
      break;
    }
    // Todo: counters added this way will delete themselves when they detect they're <= 0
      // counters that are added by dragging in from the grid will not delete themselves. nor the ones
      // added with the I/U hotkeys
    case 'addCounter':
      spawnTokenAtPosition(
        { title: '+1/+1', backgroundColor: '#e8e1df', count: 1 },
        nodeCenter(card, 'card'),
        yCards, yTokens, playerId,
      );
      break;
    case 'removeCounter':
      // Mirrors the 'I' global hotkey's cursor-position spawn (useAllGameHotkeys.ts)
      // — same template, just anchored to the card instead of the cursor.
      spawnTokenAtPosition(
        { title: '-1/-1', backgroundColor: '#e8e1df', count: -1 },
        nodeCenter(card, 'card'),
        yCards, yTokens, playerId,
      );
      break;
    case 'delete':
      useContextMenuStore.getState().close();
      detachTokens(cardId, yTokens);
      yCards.delete(cardId);
      if (yDoc) {
        const ownerName = opponentOwnerName(yDoc, card, playerId);
        logAction(yDoc, {
          actorId: playerId,
          type: 'delete',
          text: ownerName ? `removed ${ownerName}'s ${cardLogName(card)}` : `removed ${cardLogName(card)}`,
        });
      }
      break;
    // The moveTo* actions below delegate placement, deck persistence, and
    // logging to their matching battlefieldActions export (a complete
    // semantic action) — this handler only owns the battlefield-specific
    // steps: close the menu, detach any attached tokens, and remove the card
    // from the board.
    case 'moveToHand':
      useContextMenuStore.getState().close();
      detachTokens(cardId, yTokens);
      yCards.delete(cardId);
      moveCardToHand(card);
      break;
    case 'moveToDiscard':
      useContextMenuStore.getState().close();
      detachTokens(cardId, yTokens);
      yCards.delete(cardId);
      moveCardToDiscard(card);
      break;
    case 'moveToExile':
      useContextMenuStore.getState().close();
      detachTokens(cardId, yTokens);
      yCards.delete(cardId);
      moveCardToExile(card);
      break;
    case 'moveToDeckTop':
      useContextMenuStore.getState().close();
      detachTokens(cardId, yTokens);
      yCards.delete(cardId);
      moveCardToDeckTop(card);
      break;
    case 'moveToDeckBottom':
      useContextMenuStore.getState().close();
      detachTokens(cardId, yTokens);
      yCards.delete(cardId);
      moveCardToDeckBottom(card);
      break;
  }
}
